import axiosInstance from '../../services/axiosInstance';
import { PromptPdfCropRegion } from './pdfPages';
import {
  PromptTemplate,
  PromptTemplateRevision,
  PromptRun,
  RunPromptResult,
  CreateTemplateParams,
  UpdateTemplateParams,
} from './types';

// =============================================
// STATUS
// =============================================

export interface AiStatus {
  available: boolean;
  openai: boolean;
  claude: boolean;
}

export async function checkOpenAiStatus(): Promise<AiStatus> {
  const { data } = await axiosInstance.get<AiStatus>('/api/prompts/status');
  return data;
}

// =============================================
// TEMPLATES
// =============================================

export interface FetchTemplatesParams {
  search?: string;
  tag?: string;
}

export async function fetchTemplates(params?: FetchTemplatesParams): Promise<PromptTemplate[]> {
  const { data } = await axiosInstance.get<PromptTemplate[]>('/api/prompts/templates', { params });
  return data;
}

export async function fetchAllTemplates(): Promise<PromptTemplate[]> {
  const { data } = await axiosInstance.get<PromptTemplate[]>('/api/prompts/templates/all');
  return data;
}

export async function fetchTemplate(id: string): Promise<PromptTemplate> {
  const { data } = await axiosInstance.get<PromptTemplate>(`/api/prompts/templates/${id}`);
  return data;
}

export async function createTemplate(params: CreateTemplateParams): Promise<PromptTemplate> {
  const { data } = await axiosInstance.post<PromptTemplate>('/api/prompts/templates', params);
  return data;
}

export async function updateTemplate(id: string, params: UpdateTemplateParams): Promise<PromptTemplate> {
  const { data } = await axiosInstance.put<PromptTemplate>(`/api/prompts/templates/${id}`, params);
  return data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await axiosInstance.delete(`/api/prompts/templates/${id}`);
}

// =============================================
// REVISIONS
// =============================================

export async function fetchRevisions(templateId: string): Promise<PromptTemplateRevision[]> {
  const { data } = await axiosInstance.get<PromptTemplateRevision[]>(
    `/api/prompts/templates/${templateId}/revisions`
  );
  return data;
}

export async function restoreRevision(templateId: string, version: number): Promise<PromptTemplate> {
  const { data } = await axiosInstance.post<PromptTemplate>(
    `/api/prompts/templates/${templateId}/restore/${version}`
  );
  return data;
}

// =============================================
// RUN PROMPT
// =============================================

export interface RunPromptParams {
  templateId: string;
  inputText: string;
  variants?: number;
}

export async function runPrompt(params: RunPromptParams): Promise<RunPromptResult> {
  const { data } = await axiosInstance.post<RunPromptResult>('/api/prompts/run', params);
  return data;
}

export interface RunPromptWithFileParams {
  templateId: string;
  file: File;
  pages?: number[];
  crop?: PromptPdfCropRegion[];
  inputText?: string;
  variants?: number;
}

export async function runPromptWithFile(params: RunPromptWithFileParams): Promise<RunPromptResult> {
  const formData = new FormData();
  formData.append('templateId', params.templateId);
  formData.append('file', params.file);
  if (params.inputText) {
    formData.append('inputText', params.inputText);
  }
  if (params.variants != null) {
    formData.append('variants', String(params.variants));
  }
  if (params.pages?.length) {
    formData.append('pages', JSON.stringify(params.pages));
  }
  if (params.crop?.length) {
    formData.append('crop', JSON.stringify(params.crop));
  }
  const { data } = await axiosInstance.post<RunPromptResult>('/api/prompts/run-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 180000,
  });
  return data;
}

export async function runPromptClaude(params: RunPromptParams): Promise<RunPromptResult> {
  const { data } = await axiosInstance.post<RunPromptResult>('/api/prompts/run-claude', params);
  return data;
}

// =============================================
// HISTORY
// =============================================

export interface FetchHistoryParams {
  limit?: number;
  templateId?: string;
}

export async function fetchHistory(params?: FetchHistoryParams): Promise<PromptRun[]> {
  const { data } = await axiosInstance.get<PromptRun[]>('/api/prompts/history', { params });
  return data;
}

export async function fetchHistoryItem(id: string): Promise<PromptRun> {
  const { data } = await axiosInstance.get<PromptRun>(`/api/prompts/history/${id}`);
  return data;
}

// =============================================
// BRAND VOICE
// =============================================

export async function fetchBrandVoice(): Promise<{ brandVoice: string }> {
  const { data } = await axiosInstance.get<{ brandVoice: string }>('/api/prompts/brand-voice');
  return data;
}

// =============================================
// TAGS
// =============================================

export async function fetchTags(): Promise<string[]> {
  const { data } = await axiosInstance.get<string[]>('/api/prompts/tags');
  return data;
}
